import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ASSETS, LINKS } from "../lib/constants";
import { handleStoreClick } from "../lib/device";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
    { label: "Framework", hash: "framework" },
    { label: "Protocol", hash: "protocol" },
    { label: "Bloodwork", hash: "bloodwork" },
    { label: "Testimator", hash: "testimator" },
    { label: "Engine", to: "/engine" },
    { label: "Knowledge", hash: "knowledge" },
    { label: "Reviews", hash: "reviews" },
    { label: "Origin", hash: "origin" },
    { label: "FAQ", to: "/faq" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Handle anchor clicks: if we're already on landing, do a smooth scroll;
    // otherwise navigate home with a hash so the hash-scroll hook in
    // Landing picks it up.
    const onAnchorClick = (e, hash) => {
        setOpen(false);
        if (location.pathname === "/") {
            e.preventDefault();
            const el = document.getElementById(hash);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                // Keep the URL in sync without the page jump
                window.history.replaceState(null, "", `#${hash}`);
            }
        } else {
            e.preventDefault();
            navigate(`/#${hash}`);
        }
    };

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-black/80 backdrop-blur-xl border-b border-white/5"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-center gap-2.5"
                >
                    <img
                        src={ASSETS.icon}
                        alt="TRT+"
                        className="h-8 w-8 rounded-md"
                    />
                    <span className="font-brand text-2xl leading-none hidden sm:inline">
                        <span className="text-white">TRT</span>
                        <span className="text-[#2ba6e0]">+</span>
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {NAV_LINKS.map((l) =>
                        l.to ? (
                            <Link
                                key={l.label}
                                to={l.to}
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                                className="px-4 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
                            >
                                {l.label}
                            </Link>
                        ) : (
                            <a
                                key={l.label}
                                href={`/#${l.hash}`}
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                                onClick={(e) => onAnchorClick(e, l.hash)}
                                className="px-4 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
                            >
                                {l.label}
                            </a>
                        )
                    )}
                </nav>

                <div className="flex items-center gap-2">
                    <a
                        href={LINKS.appStore}
                        onClick={handleStoreClick}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="nav-cta-download"
                        className="hidden sm:inline-flex btn-blue rounded-full px-5 py-2.5 text-sm"
                    >
                        Download
                    </a>
                    <button
                        className="lg:hidden p-2 rounded-md border border-white/10 text-white"
                        onClick={() => setOpen(!open)}
                        data-testid="nav-menu-toggle"
                        aria-label="Menu"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/5"
                    data-testid="mobile-menu"
                >
                    <div className="px-6 py-4 flex flex-col gap-1">
                        {NAV_LINKS.map((l) =>
                            l.to ? (
                                <Link
                                    key={l.label}
                                    to={l.to}
                                    onClick={() => setOpen(false)}
                                    className="py-3 text-base font-medium text-white/80 hover:text-white border-b border-white/5"
                                >
                                    {l.label}
                                </Link>
                            ) : (
                                <a
                                    key={l.label}
                                    href={`/#${l.hash}`}
                                    onClick={(e) => onAnchorClick(e, l.hash)}
                                    className="py-3 text-base font-medium text-white/80 hover:text-white border-b border-white/5"
                                >
                                    {l.label}
                                </a>
                            )
                        )}
                        <a
                            href={LINKS.appStore}
                            onClick={handleStoreClick}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="nav-mobile-cta-download"
                            className="mt-3 btn-blue rounded-full px-5 py-3 text-sm text-center"
                        >
                            Download the app
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
